import { CheckCircle2, Clock, Gauge, GraduationCap, Send, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { SurveyTrainingImpactModal } from '../components/SurveyTrainingImpactModal';
import { SurveyTrainingRecommendationPersonnelPanel } from '../components/SurveyTrainingRecommendationPersonnelPanel';
import { SurveyTrainingRecommendationModal } from '../components/SurveyTrainingRecommendationModal';
import { SurveyTrainingRecommendationReviewModal } from '../components/SurveyTrainingRecommendationReviewModal';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import {
  TRAINING_RECOMMENDATION_STATUS,
  TRAINING_RECOMMENDATION_STATUS_LABELS,
  type SurveyTrainingRecommendationDetail,
  type TrainingRecommendationStatusValue,
} from '../surveyTypes';

type TabKey = 'info' | 'approval' | 'personnel';

const TONES: Record<TrainingRecommendationStatusValue, StatusTone> = { 0: 'inactive', 1: 'pending', 2: 'active', 3: 'blocked' };

/**
 * A training recommendation's profile — what a plan's Training Recommendations Title opens
 * (product call, 12 Oct 2026).
 *
 * - **Info** — its details and the document recommending it.
 * - **Approval** — its approval in THREE STEPS, in order: submitted for approval, decided (the
 *   status, who decided and when, and their remarks), then SENT to the employees on its plan —
 *   which is what lets them read it; approved is not the same as sent. The CHAIN of approvers is
 *   linked, not repeated — it is the approval's own page under Approvals.
 * - **Personnel** — the employees on the plan it belongs to (moved here from the plan's profile).
 *
 * Submit for approval is the banner's action, and confirms — the record has moved by the time the
 * dialog closes. Edit is the cards' own, disabled with the reason while an approver holds it.
 */
export function SurveyTrainingRecommendationProfilePage({ from = 'survey' }: { from?: 'survey' | 'training-recommendations' }) {
  const { id } = useParams<{ id: string }>();
  /*
    ONE profile, two homes — the arrangement a development plan's takes: opened from a plan's own
    Training Recommendations tab, under its survey, or from Learning & Development › Training and
    Recommendations, whose register spans every plan. The second is on that module's own key, and
    its breadcrumb leads back to where the reader came from. Everything drawn is the same; each
    control still carries the key of what it does.
  */
  const fromRegister = from === 'training-recommendations';
  const canOpenPlans = usePermission('development-plans.read');
  const canSeeApprovals = usePermission('approvals.read');
  const canDecideApprovals = usePermission('approvals.decide');
  const toast = useToast();

  const [detail, setDetail] = useState<SurveyTrainingRecommendationDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // LANDS ON PERSONNEL (product call, 13 Oct 2026): the profile is opened to see who is on the
  // recommendation and where each stands. `Info` is still the first tab DECLARED.
  const [tab, setTab] = useState<TabKey>('personnel');
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [running, setRunning] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [sending, setSending] = useState(false);
  const [assessingImpact, setAssessingImpact] = useState(false);
  // Bumped after an impact assessment is saved, so the Personnel table reads its new dates.
  const [personnelVersion, setPersonnelVersion] = useState(0);
  // ONE piece of state for which decision is pending — never two booleans.
  const [deciding, setDeciding] = useState<'approve' | 'decline' | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      surveyTrainingRecommendationsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDetail(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this training recommendation.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function runSubmit() {
    if (!detail) return;

    setRunning(true);
    setSubmitError(null);

    try {
      setDetail(await surveyTrainingRecommendationsApi.submit(detail.recommendation.id));
      toast.success('Submitted the training recommendation for approval.');
      setSubmitting(false);
    } catch (caught) {
      setSubmitError(caught instanceof ApiError ? caught : new ApiError('It could not be submitted.', 0));
    } finally {
      setRunning(false);
    }
  }

  async function runSend() {
    if (!detail) return;

    setRunning(true);
    setSubmitError(null);

    try {
      setDetail(await surveyTrainingRecommendationsApi.send(detail.recommendation.id));
      toast.success('Sent the training recommendation to the employees.');
      setSending(false);
    } catch (caught) {
      setSubmitError(caught instanceof ApiError ? caught : new ApiError('It could not be sent.', 0));
    } finally {
      setRunning(false);
    }
  }

  // The plan, in the home the reader is in — the register's reader need not hold Survey's read.
  const planPath = !detail
    ? null
    : fromRegister
      ? canOpenPlans
        ? `/learning-development/development-plan/${detail.recommendation.surveyPlanId}`
        : null
      : `/learning-development/survey/${detail.surveyId}/plan/${detail.recommendation.surveyPlanId}`;

  const breadcrumb: BreadcrumbSegment[] = fromRegister
    ? [
        'Learning & Development',
        { label: 'Training Recommendations', to: '/learning-development/training-recommendation' },
        detail ? detail.recommendation.title : 'Training recommendation',
      ]
    : [
        'Learning & Development',
        { label: 'Survey', to: '/learning-development/survey' },
        ...(detail && planPath
          ? [
              { label: detail.surveyTitle, to: `/learning-development/survey/${detail.surveyId}` },
              { label: detail.planTitle, to: planPath },
            ]
          : []),
        detail ? detail.recommendation.title : 'Training recommendation',
      ];

  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { recommendation, approval } = detail;
  const status = recommendation.status;
  const deleted = !recommendation.isActive;
  // `!= null`: an unsent one arrives with the field absent.
  const sent = recommendation.sentAt != null;

  // Why it cannot be changed right now — a STATE, so the Edit is disabled with the reason, never hidden.
  const editDisabledReason = deleted
    ? 'This training recommendation is deleted.'
    : status === TRAINING_RECOMMENDATION_STATUS.Submitted
      ? 'This training recommendation is waiting for approval. Retract it from Approvals first.'
      : status === TRAINING_RECOMMENDATION_STATUS.Approved
        ? 'This training recommendation has been approved, so it can no longer be changed.'
        : null;

  /*
    THE DECISION, in the banner — offered only to the approver the request is WAITING ON: the
    engine's own answer for this reader, plus the engine's key. Everyone else reads the sentence.
    The engine refuses anybody else either way.
  */
  const canDecide =
    !deleted && status === TRAINING_RECOMMENDATION_STATUS.Submitted && approval?.canDecide === true && canDecideApprovals;

  const canSubmit =
    !deleted && (status === TRAINING_RECOMMENDATION_STATUS.NotSubmitted || status === TRAINING_RECOMMENDATION_STATUS.Rejected);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="training-recommendation-profile"
        header={
          <ProfileHeader
            id="training-recommendation-profile-header"
            icon={<GraduationCap className="h-6 w-6" aria-hidden="true" />}
            title={recommendation.title}
            meta={[detail.planTitle]}
            secondary={[detail.surveyTitle]}
            status={
              <StatusText tone={deleted ? 'inactive' : TONES[status]}>
                {deleted ? 'Deleted' : TRAINING_RECOMMENDATION_STATUS_LABELS[status]}
              </StatusText>
            }
            createdBy={recommendation.createdByName}
            createdAt={recommendation.createdAt}
          />
        }
        banner={
          deleted ? (
            <ProfileBanner
              id="training-recommendation-profile-banner"
              tone="red"
              icon={<XCircle className="h-4 w-4" aria-hidden="true" />}
              message="This training recommendation is deleted. Nothing is erased."
            />
          ) : status === TRAINING_RECOMMENDATION_STATUS.Approved ? (
            <ProfileBanner
              id="training-recommendation-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                sent
                  ? 'This training recommendation is approved and has been sent to the employees on its plan.'
                  : 'This training recommendation is approved. It has not been sent to the employees yet — they see nothing until it is.'
              }
              // It WRITES — the employees can read it the moment it lands — so it confirms.
              action={
                !sent ? (
                  <PermissionGate permission="surveys.send-plan">
                    <Button
                      id="training-recommendation-send"
                      variant="primary"
                      onClick={() => setSending(true)}
                      leading={<Send className="h-4 w-4" aria-hidden="true" />}
                    >
                      Send to employees
                    </Button>
                  </PermissionGate>
                ) : (
                  // SENT: what is left is what the training changed. It opens a form — the form's own tick is the gate.
                  <PermissionGate permission="surveys.assess-impact">
                    <Button
                      id="training-recommendation-impact"
                      variant="primary"
                      onClick={() => setAssessingImpact(true)}
                      leading={<Gauge className="h-4 w-4" aria-hidden="true" />}
                    >
                      Impact assessment
                    </Button>
                  </PermissionGate>
                )
              }
            />
          ) : status === TRAINING_RECOMMENDATION_STATUS.Submitted ? (
            <ProfileBanner
              id="training-recommendation-profile-banner"
              tone="slate"
              icon={<Clock className="h-4 w-4" aria-hidden="true" />}
              message={
                canDecide
                  ? 'This training recommendation is waiting for your approval. Read it under Info, then approve or decline it.'
                  : 'This training recommendation is waiting for approval under Approvals. It cannot be changed while the approver holds it.'
              }
              // Each only OPENS the decision's form, whose remarks and tick are the confirmation.
              action={
                canDecide ? (
                  <div className="flex items-center gap-2">
                    <Button id="training-recommendation-decline" variant="secondary" onClick={() => setDeciding('decline')}>
                      Decline
                    </Button>
                    <Button id="training-recommendation-approve" variant="primary" onClick={() => setDeciding('approve')}>
                      Approve
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <ProfileBanner
              id="training-recommendation-profile-banner"
              tone="slate"
              icon={<Send className="h-4 w-4" aria-hidden="true" />}
              message={
                status === TRAINING_RECOMMENDATION_STATUS.Rejected
                  ? 'This training recommendation was rejected — the remarks are under Approval. Revise it, then submit it again.'
                  : 'This training recommendation has not been submitted for approval yet.'
              }
              // The ACTION is gated, not the banner — the state is worth stating to a reader who cannot submit.
              action={
                canSubmit ? (
                  <PermissionGate permission="surveys.submit-plan">
                    <Button
                      id="training-recommendation-submit"
                      variant="primary"
                      onClick={() => setSubmitting(true)}
                      leading={<Send className="h-4 w-4" aria-hidden="true" />}
                    >
                      Submit for approval
                    </Button>
                  </PermissionGate>
                ) : undefined
              }
            />
          )
        }
        // The Personnel table owns its own chrome, so its tab runs edge to edge; the others keep the card column.
        panel={tab === 'personnel' ? 'bleed' : 'cards'}
        tabs={[
          { key: 'info', label: 'Info' },
          // The recommendation's approval, step by step (product call, 13 Oct 2026).
          { key: 'approval', label: 'Approval' },
          {
            key: 'personnel',
            label: (
              <>
                Personnel <CountBadge count={detail.memberCount} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="training-recommendation-profile-details"
              title="Details"
              onEdit={() => setEditing(true)}
              editPermission="surveys.plan"
              editDisabledReason={editDisabledReason}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: recommendation.title },
                  {
                    label: 'Development plan',
                    // A link only where the reader can open the page behind it.
                    value: planPath ? (
                      <Link to={planPath} className="font-medium text-slate-800 hover:underline">
                        {detail.planTitle}
                      </Link>
                    ) : (
                      detail.planTitle
                    ),
                  },
                  { label: 'Survey', value: detail.surveyTitle },
                ]}
              />
            </ProfileCard>

            {/* A derived view of what Details' Edit writes — the one form holds both, so one Edit. */}
            <ProfileCard id="training-recommendation-profile-document" title="Recommendation" padded>
              {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
              <RichTextDocument html={detail.details ?? null} />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="training-recommendation-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: recommendation.createdByName },
                  { label: 'Created', value: formatDateTime(recommendation.createdAt), mono: true },
                  { label: 'Updated by', value: recommendation.updatedByName },
                  {
                    label: 'Updated',
                    value: recommendation.updatedAt != null ? formatDateTime(recommendation.updatedAt) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'approval' ? (
          <>
            {/*
              THE APPROVAL, IN ITS THREE STEPS, in the order they happen: submitted for approval,
              decided, then sent to the employees. Each card is one step; a step not reached yet
              shows its rows as dashes, so the three always read as the same record. No Edit on
              any — each is moved by an ACTION (the banner's), never typed here.
            */}
            <ProfileCard id="training-recommendation-profile-approval-submit" title="Submit for approval">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Submitted', value: approval ? formatDateTime(approval.submittedAt) : null, mono: true },
                  { label: 'Submitted by', value: approval?.submittedByName },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="training-recommendation-profile-approval" title="Approval">
              <PreviewTable
                variant="table-display"
                rows={[
                  // Before it is submitted there is no request: the recommendation's own status says so.
                  { label: 'Status', value: approval ? approval.statusLabel : TRAINING_RECOMMENDATION_STATUS_LABELS[status] },
                  { label: 'Decided', value: approval?.decidedAt != null ? formatDateTime(approval.decidedAt) : null, mono: true },
                  { label: 'Decided by', value: approval?.decidedByName },
                  { label: 'Remarks', value: approval?.remarks },
                  ...(approval
                    ? [
                        {
                          // The chain is the approval's own page — linked, not repeated.
                          label: 'Chain',
                          value: canSeeApprovals ? (
                            <Link to={`/approvals/requests/${approval.requestId}`} className="font-medium text-slate-800 hover:underline">
                              Open the approval
                            </Link>
                          ) : (
                            'Hidden'
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </ProfileCard>

            <ProfileCard id="training-recommendation-profile-approval-send" title="Send to employees">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Sent', value: recommendation.sentAt != null ? formatDateTime(recommendation.sentAt) : null, mono: true },
                  { label: 'Sent by', value: recommendation.sentByName },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /*
            PERSONNEL — the standard table (product call, 13 Oct 2026): the plan's employees with
            each one's progress on this recommendation, Create to add more, and a name opening
            that employee's answer, training and report.
          */
          <SurveyTrainingRecommendationPersonnelPanel
            key={personnelVersion}
            recommendationId={recommendation.id}
            surveyId={detail.surveyId}
            planId={recommendation.surveyPlanId}
            planTitle={detail.planTitle}
            readPermission={fromRegister ? 'training-recommendations.read' : 'surveys.read'}
            canAdd={!deleted}
            onChanged={() => load()}
          />
        )}
      </ProfileHeaderShell>

      {editing ? (
        <SurveyTrainingRecommendationModal
          open
          planId={recommendation.surveyPlanId}
          editing={recommendation}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {/*
        The banner's Approve and Decline open the recommendation's own Review form on that decision
        — its remarks (optional to approve, required to decline) and its tick are the confirmation.
        One form for a decision, here and from the plan's table.
      */}
      {deciding ? (
        <SurveyTrainingRecommendationReviewModal
          open
          recommendationId={recommendation.id}
          title={recommendation.title}
          initialStage={deciding}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            load();
          }}
        />
      ) : null}

      {assessingImpact ? (
        <SurveyTrainingImpactModal
          open
          recommendation={recommendation}
          onClose={() => setAssessingImpact(false)}
          onSaved={() => {
            setAssessingImpact(false);
            setPersonnelVersion((v) => v + 1);
          }}
        />
      ) : null}

      {sending ? (
        <ConfirmDialog
          open
          id="training-recommendation-send-dialog"
          title="Send training recommendation"
          requireConfirm
          loading={running}
          onConfirm={() => void runSend()}
          onClose={() => {
            setSending(false);
            setSubmitError(null);
          }}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {`The approved recommendation is sent to the ${detail.memberCount === 1 ? 'employee' : `${detail.memberCount} employees`} on its plan, who can read it from then on. It cannot be unsent.`}
              </p>

              {submitError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {submitError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {submitting ? (
        <ConfirmDialog
          open
          id="training-recommendation-submit-dialog"
          title="Submit training recommendation"
          requireConfirm
          loading={running}
          onConfirm={() => void runSubmit()}
          onClose={() => {
            setSubmitting(false);
            setSubmitError(null);
          }}
          body={
            <div className="flex flex-col gap-2">
              <p>
                The training recommendation goes to its approver under Approvals. It cannot be changed while they hold
                it, or once it is approved.
              </p>

              {submitError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {submitError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
